import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import { getMuiTablePaginationI18nProps } from 'components/table/muiTablePaginationI18nProps';
import {
  REPORT_CONTACT_OVERVIEW_COUNT_QUERY,
  REPORT_CONTACT_OVERVIEW_LIST_QUERY,
} from '../gql';
import {
  ContactOverviewReportFiltersInput,
  ContactOverviewReportSortInput,
  EReportsSortDirection,
  ReportContactOverviewListQuery,
} from '__generated__/graphql';
import ContactOverviewFilters from './components/ContactOverviewFilters';
import SortableContactOverviewHeader from './components/SortableContactOverviewHeader';

const formatReportContactType = (val?: string | null) => {
  if (!val || val === '-') return String(i18next.t('comment-contact-type-info'));
  if (val === 'PERS') return String(i18next.t('comment-contact-type-personal'));
  if (val === 'EMAIL') return String(i18next.t('comment-contact-type-email'));
  if (val === 'TEL') return String(i18next.t('comment-contact-type-phone'));
  if (val === 'INFO') return String(i18next.t('comment-contact-type-info'));
  return val;
};

export default function ContactOverviewTab() {
  const [filters, setFilters] = useState<ContactOverviewReportFiltersInput>({});
  const [results, setResults] = useState<ReportContactOverviewListQuery['reportContactOverviewList']>([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 50,
    totalCount: 0,
  });
  const [sortFields, setSortFields] = useState<ContactOverviewReportSortInput[]>([
    { timestamp: EReportsSortDirection.desc },
  ]);

  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat(i18next.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const [executeListQuery] = useLazyQuery(REPORT_CONTACT_OVERVIEW_LIST_QUERY, {
    fetchPolicy: 'network-only',
  });
  const [executeCountQuery] = useLazyQuery(REPORT_CONTACT_OVERVIEW_COUNT_QUERY, {
    fetchPolicy: 'network-only',
  });

  const handleFilterChange = (field: keyof ContactOverviewReportFiltersInput, value: any) => {
    setFilters((prev) => {
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        const newFilters = { ...prev };
        delete newFilters[field];
        return newFilters;
      }
      return { ...prev, [field]: value };
    });
  };

  const handleReset = () => {
    setFilters({});
    setResults([]);
    setPagination({ page: 0, rowsPerPage: 50, totalCount: 0 });
    setSortFields([{ timestamp: EReportsSortDirection.desc }]);
  };

  const handleGenerateReport = async (page: number, rowsPerPage: number) => {
    setLoading(true);

    try {
      const [countResult, listResult] = await Promise.all([
        executeCountQuery({ variables: { filters } }),
        executeListQuery({
          variables: {
            filters,
            sort: sortFields,
            skip: page * rowsPerPage,
            take: rowsPerPage,
          },
        }),
      ]);

      const totalCount = countResult.data?.reportContactOverviewCount || 0;
      const list = listResult.data?.reportContactOverviewList || [];

      setResults(list);
      setPagination((prev) => ({ ...prev, totalCount, page }));
    } catch (error) {
      console.error('Contact Overview query error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (field: keyof ContactOverviewReportSortInput, direction?: EReportsSortDirection) => {
    let newSortFields: ContactOverviewReportSortInput[];
    
    const currentSort = sortFields.find((s) => s[field]);
    
    if (currentSort) {
      const currentDirection = currentSort[field];
      if (currentDirection === EReportsSortDirection.asc) {
        newSortFields = [{ [field]: EReportsSortDirection.desc }];
      } else {
        newSortFields = [{ [field]: EReportsSortDirection.asc }];
      }
    } else {
      newSortFields = [{ [field]: EReportsSortDirection.asc }];
    }
    
    setSortFields(newSortFields);
  };

  useEffect(() => {
    if (results.length > 0) {
      handleGenerateReport(pagination.page, pagination.rowsPerPage);
    }
  }, [sortFields]);

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    handleGenerateReport(newPage, pagination.rowsPerPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setPagination((prev) => ({ ...prev, rowsPerPage: newRowsPerPage, page: 0 }));
    handleGenerateReport(0, newRowsPerPage);
  };

  const handleExportExcel = async () => {
    setExportLoading(true);

    try {
      const result = await executeListQuery({
        variables: {
          filters,
          sort: sortFields,
          skip: 0,
          take: undefined,
        },
      });

      const allResults = result.data?.reportContactOverviewList || [];

      // Create CSV headers
      const headers = [
        String(i18next.t('contact-person')),
        String(i18next.t('filter-company')),
        String(i18next.t('contact-timestamp')),
        String(i18next.t('contact-subject')),
        String(i18next.t('contact-employee')),
        String(i18next.t('contact-type')),
      ];

      // Create CSV rows
      const csvData = allResults.map((row) => [
        row.contactPersonName || '',
        row.companyName || '',
        formatDateTime(new Date(row.timestamp)),
        row.subject || '',
        row.employeeName || '',
        formatReportContactType(row.contactType),
      ]);

      // Generate CSV content with proper escaping
      const csvContent = [headers, ...csvData]
        .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        .join('\n');

      // Create blob and download with UTF-8 BOM for Excel compatibility
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `contact-overview_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Box component="form" sx={{ width: '100%' }}>
      <ContactOverviewFilters filters={filters} onFilterChange={handleFilterChange} />

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 4 }}>
        <Button variant="contained" color="primary" onClick={() => handleGenerateReport(0, pagination.rowsPerPage)} disabled={loading}>
          {loading ? <CircularProgress size={20} /> : i18next.t('filter-generate-report')}
        </Button>
        <Button variant="outlined" onClick={handleReset}>
          {i18next.t('filter-reset')}
        </Button>
      </Box>

      {loading && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{i18next.t('report-loading')}</Typography>
        </Box>
      )}

      {!loading && results.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {i18next.t('statistics-results')} ({pagination.totalCount} {i18next.t('statistics-entries-found')})
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <SortableContactOverviewHeader
                    field="contactPerson"
                    label={String(i18next.t('contact-person'))}
                    sortFields={sortFields}
                    onSort={handleSortChange}
                  />
                  <SortableContactOverviewHeader
                    field="company"
                    label={String(i18next.t('filter-company'))}
                    sortFields={sortFields}
                    onSort={handleSortChange}
                  />
                  <SortableContactOverviewHeader
                    field="timestamp"
                    label={String(i18next.t('contact-timestamp'))}
                    sortFields={sortFields}
                    onSort={handleSortChange}
                  />
                  <SortableContactOverviewHeader
                    field="subject"
                    label={String(i18next.t('contact-subject'))}
                    sortFields={sortFields}
                    onSort={handleSortChange}
                  />
                  <SortableContactOverviewHeader
                    field="employee"
                    label={String(i18next.t('contact-employee'))}
                    sortFields={sortFields}
                    onSort={handleSortChange}
                  />
                  <SortableContactOverviewHeader
                    field="contactType"
                    label={String(i18next.t('contact-type'))}
                    sortFields={sortFields}
                    onSort={handleSortChange}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.contactPersonName}</TableCell>
                    <TableCell>{row.companyName}</TableCell>
                    <TableCell>{formatDateTime(new Date(row.timestamp))}</TableCell>
                    <TableCell>{row.subject}</TableCell>
                    <TableCell>{row.employeeName}</TableCell>
                    <TableCell>{formatReportContactType(row.contactType)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={pagination.totalCount}
            page={pagination.page}
            onPageChange={(_, newPage) => handleChangePage(newPage)}
            rowsPerPage={pagination.rowsPerPage}
            onRowsPerPageChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[25, 50, 100]}
            {...getMuiTablePaginationI18nProps()}
          />
          {results.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={exportLoading ? <CircularProgress size={20} /> : <GetAppIcon />}
                onClick={handleExportExcel}
                disabled={exportLoading}
              >
                {i18next.t('download-csv-report')}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}